import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { isEqual } from "lodash";

import { Auth, fetchOwnUserDetails, refreshToken, updatePreferences } from "src/api/user.ts";
import { User, UserPreferences } from "src/model/user.ts";
import { RootState, useAppDispatch } from "src/store/store.ts";

export type UserRole = "User" | "Admin";

export const UserRights = ["CreateSubscription", "ViewSubscriptionReport", "ViewContainerReport"] as const;

export type UserRight = (typeof UserRights)[number];

interface AccessToken {
  Name: string;
  Role: UserRole;
  UserId: string;
  CustomerId?: string;
  CompanyId?: string;
  exp: number;
}

interface AdminUserState {
  id: number;
  username: string;
  role: "Admin";
}

interface UserUserState {
  id: number;
  username: string;
  role: "User";
  rights: UserRight[];
  customerId: string;
  companyId: string | null;
}

export type UserState = AdminUserState | UserUserState;

export interface AuthState {
  accessToken: string;
  expiry: number;
  refreshToken: string;
}

interface AuthReducerState {
  user: UserState | null | undefined;
  preferences: UserPreferences | null | undefined;
}

const initialState: AuthReducerState = {
  user: undefined,
  preferences: undefined,
};

export const parseAccessToken = (accessTokenString: string): AccessToken => {
  const accessTokenParts = accessTokenString.split(".");
  if (accessTokenParts[1] !== undefined) {
    return JSON.parse(atob(accessTokenParts[1])) as AccessToken;
  }
  throw new Error("Unable to parse access token of user");
};

const mapAuth = (user: Auth): AuthState => {
  const accessToken = parseAccessToken(user.accessToken);

  return {
    accessToken: user.accessToken,
    refreshToken: user.refreshToken,
    expiry: accessToken.exp,
  };
};

const mapUserToState = (user: User): UserState => {
  if (user.userRole === "Admin") {
    return {
      id: user.id,
      username: user.userName,
      role: user.userRole,
    };
  } else {
    return {
      id: user.id,
      role: user.userRole,
      rights: user.userRights,
      username: user.userName,
      customerId: user.customerId!,
      companyId: user.companyId,
    };
  }
};

const isTokenExpired = (expiry: number): boolean => {
  return expiry * 1000 < Date.now() - 60000;
};

export const setAuth = (auth: Auth) => {
  localStorage.setItem("auth", JSON.stringify(mapAuth(auth)));
};

export const refreshAuth = async (existingAuth: AuthState | undefined = undefined): Promise<void> => {
  const authState = existingAuth ?? loadAuthState();
  if (authState !== null && isTokenExpired(authState.expiry)) {
    const auth = await refreshToken(authState.refreshToken);
    setAuth(auth);
  }
};

export const loadAuthState = (): AuthState | null => {
  const authString = localStorage.getItem("auth");
  if (authString === null) {
    return null;
  }

  return JSON.parse(authString) as AuthState;
};

export const initialize = async (dispatch: ReturnType<typeof useAppDispatch>): Promise<void> => {
  const auth = loadAuthState();

  if (auth === null) {
    dispatch(clearUser());
    return;
  }

  try {
    await refreshAuth(auth);
  } catch (error) {
    dispatch(clearUser());
    return;
  }

  const user = await fetchOwnUserDetails();
  dispatch(setUser(user));
  dispatch(setPreferences(user.preferences ?? {}));
};

export const updateUserPreferences = (preferences: UserPreferences) => {
  return (dispatch: ReturnType<typeof useAppDispatch>, getState: () => RootState) => {
    const state = getState();
    const userState = state.auth.preferences;
    if (userState !== undefined && userState !== null) {
      const newPreferences: UserPreferences = {
        ...userState,
        ...preferences,
      };
      if (!isEqual(userState, newPreferences)) {
        updatePreferences(newPreferences).catch((error) => console.error(error));
        dispatch(setPreferences(newPreferences));
      }
    }
  };
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      return { ...state, user: mapUserToState(action.payload) };
    },
    setPreferences: (state, action: PayloadAction<UserPreferences | null>) => {
      return { ...state, preferences: action.payload };
    },
    clearUser: () => {
      localStorage.removeItem("auth");
      return { user: null, preferences: null };
    },
  },
});

export const authReducer = authSlice.reducer;
export const setUser = authSlice.actions.setUser;
export const setPreferences = authSlice.actions.setPreferences;
export const clearUser = authSlice.actions.clearUser;
